import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getLists } from "../../../reducers/lists";
import { getCurrentUser } from "../../../reducers/currentUser";
import useDebounce from "../../../hooks";
import Page from "../Page";
import Parse from "parse";
import DataGridView from "../../../components/reactgrid/DataGridView";
import HandleGroupForm from "../../form/HandleGroupForm";
import {
  makeStyles,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Snackbar,
  Toolbar,
  IconButton,
  AppBar,
  Grid,
} from "@material-ui/core";
import {
  setNumberColumns,
  filtersQuery,
  translateColumn,
} from "../../../utils2";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";

const useStyles = makeStyles(() => ({
  tableTopWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "",
  },
  tableTitleWrapper: {
    display: "flex",
    alignItems: "center",
    minHeight: "64px",
    padding: "0 8px 0 24px",
  },
  subtitle1Wrapper: {
    padding: "12px 0 24px",
  },
  toolbar: {
    backgroundColor: "#46494c", //'#02cfa3'
  },
}));
const pageSizes = [15, 25, 50, 100, 150];

const Groups = () => {
  const classes = useStyles();
  const listsOptions = useSelector(getLists);
  const currentUser = useSelector(getCurrentUser);
  const userRole =
    currentUser !== undefined ? currentUser.get("role") : undefined;
  let columns = [
    { name: "CLKTCODE", title: "Code", type: "string" },
    { name: "CLCTNOM", title: "Nom", type: "string" },
    { name: "CLCTREP1", title: "Représentant", type: "string" },
    { name: "RFAFix", title: "RFA (€)", type: "numeric" },
    { name: "RFAPercentage", title: "RFA (%)", type: "numeric" },
  ];
  if (userRole === "commercial") {
    columns.splice(2, 1); // removing CLCTREP1
  }
  const columnExtensions = [
    { columnName: "CLKTCODE" },
    { columnName: "CLCTNOM" },
    { columnName: "CLCTREP1" },
  ];
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState([]);
  const debouncedFilters = useDebounce(filters, 300);
  const [sorting, setSorting] = useState([
    { columnName: "CLCTNOM", direction: "asc" },
  ]);
  const [totalCount, setTotalCount] = useState(100);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [columnsTranslations, setColumnsTranslations] = useState([]);
  const [numberColumns, setStateNumberColumns] = useState([]);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarText, setSnackBarText] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupCode, setGroupCode] = useState(null);

  /********************************************************/
  /************ content info for paging panel *************/
  /********************************************************/
  const contentInfo = ({ from, to, count }) => {
    return `${from} ${from < to ? ` à ${to}` : ""} sur ${count} groupement(s)`;
  };

  /********************************************************/
  /*********************** Table data *********************/
  /********************************************************/
  const changeFilters = (filters) => {
    setLoading(true);
    setFilters(filters);
  };
  const changeSorting = (sorting) => {
    setLoading(true);
    setSorting(sorting);
  };
  const changeCurrentPage = (currentPage) => {
    setLoading(true);
    setCurrentPage(currentPage);
  };
  const changePageSize = (pageSize) => setPageSize(pageSize);

  const listToColumnTranslations = (arrayOfListNameAndColumnName) => {
    const newColumnTranslations = [];
    arrayOfListNameAndColumnName.forEach((listNameAndColumnName) => {
      let listOfOptions = [];
      // getting list
      listsOptions.forEach((list) => {
        if (list.listName === listNameAndColumnName.listName) {
          listOfOptions = list.options;
        }
      });
      newColumnTranslations.push({
        columnName: listNameAndColumnName.columnName,
        values: listOfOptions.map((option) => [option.value, option.text]),
      });
    });
    setColumnsTranslations([...columnsTranslations, ...newColumnTranslations]);
  };

  // getting clients from mongo
  const getGroups = async () => {
    if (!columnsTranslations.length) {
      return null;
    }
    const query = await new Parse.Query("Groups").select(
      "CLKTCODE",
      "CLCTNOM",
      "CLCTREP1",
      "RFAFix",
      "RFAPercentage",
    );

    if (userRole === "commercial") {
      query.equalTo("CLCTREP1", currentUser.get("commercial"));
    }
    filtersQuery(query, columnsTranslations, { columns, filters, sorting });
    const totalCount = await query.count();

    // skipping according to currentPage and pageSize
    query.skip(pageSize * currentPage);
    query.limit(pageSize);

    const groups = await query.find();

    const rows = groups.map((client) => {
      const row = {};
      columns.forEach((column) => {
        let value = client.get(column.name);
        if (column.name === "CLCTREP1") {
          row[column.name] = translateColumn(
            value,
            columnsTranslations.find(
              (columnTranslation) =>
                columnTranslation.columnName === "CLCTREP1",
            ),
          );
          row[column.name] = row[column.name] !== "0" ? row[column.name] : "-";
        } else if (
          column.name === "RFAFix" ||
          column.name === "RFAPercentage"
        ) {
          row[column.name] = value !== undefined ? value : 0;
        } else {
          row[column.name] = value;
        }
      });
      return row;
    });
    setRows(rows);
    setTotalCount(totalCount);
    setLoading(false);
  };

  /********************************************************/
  /*********************** Dialog *************************/
  /********************************************************/
  // when dialog is called
  const handleClickOpen = async (groupCode) => {
    setGroupCode(groupCode);
    setDialogOpen(true);
  };
  // when closing dialog box
  const handleModalClose = async (action) => {
    if (action === "validate") {
      handleSnackBarShow("groupement mis à jour");
    }
    if (action === "pmiError") {
      handleSnackBarShow("erreur de mise à jour (PMI)");
    }
    // we always reload groups on modal close
    await getGroups();
    setDialogOpen(false);
  };

  /********************************************************/
  /************************ SnackBar **********************/
  /********************************************************/
  const handleSnackBarShow = (textValue) => {
    setSnackBarOpen(true);
    setSnackBarText(textValue);
  };

  const handleSnackBarClose = () => {
    setSnackBarOpen(false);
  };

  /********************************************************/
  /************************ Actions ***********************/
  /********************************************************/
  const displayIcon = (action, row) => {
    if (row === undefined) {
      return false;
    } else {
      if (action === "editGroup") {
        return userRole !== "commercial";
      } else if (action === "showGroup") {
        return userRole === "commercial";
      } else {
        return true;
      }
    }
  };

  const actions = [
    {
      icon: <EditIcon />,
      action: (rowData) => handleClickOpen(rowData.row.CLKTCODE),
      displayIcon: (rowData) => displayIcon("editGroup", rowData.row),
    },
    {
      icon: <InfoIcon />,
      action: (rowData) => handleClickOpen(rowData.row.CLKTCODE),
      displayIcon: (rowData) => displayIcon("showGroup", rowData.row),
    },
  ];

  /********************************************************/
  /*********************** useEffects *********************/
  /********************************************************/
  useEffect(() => {
    setNumberColumns(columns, setStateNumberColumns);
    listToColumnTranslations([
      { listName: "commercials", columnName: "CLCTREP1" },
    ]);
    // eslint-disable-next-line
  }, []);

  // when page or sorting change or when columnsTranslations is loaded
  useEffect(() => {
    getGroups();
    // eslint-disable-next-line
  }, [currentPage, sorting, pageSize, columnsTranslations]);

  // when filters change
  useEffect(() => {
    if (debouncedFilters) {
      getGroups();
    }
    // eslint-disable-next-line
  }, [debouncedFilters]);

  if (!currentUser.get("clientAble") && userRole !== "commercial") {
    return null;
  }
  return (
    <Page
      toolBarTitle="Gestion des groupements"
      pageTitle="Gestion des groupements"
    >
      {/*<Typography variant="h2">Gestion des magasins</Typography>*/}
      <div className={classes.subtitle1Wrapper}>
        <Typography variant="subtitle1" paragraph>
          Editez certains champs des groupements depuis cette page.
          <br />
          Les groupements peuvent être supprimés ou ajoutés uniquement depuis
          PMI. Les champs éditables sont ceux non présents dans PMI.
        </Typography>
      </div>
      <Paper>
        <DataGridView
          title="Liste des groupements"
          rowsValues={rows}
          columnHeaders={columns}
          currentPage={currentPage}
          onCurrentPageChange={changeCurrentPage}
          pageSize={pageSize}
          pageSizes={pageSizes}
          onPageSizeChange={changePageSize}
          filters={filters}
          onFiltersChange={changeFilters}
          sorting={sorting}
          onSortingChange={changeSorting}
          totalCount={totalCount}
          loading={loading}
          numberColumns={numberColumns}
          rowsPerPage="Nb groupement par page:"
          contentInfoSupplier={contentInfo}
          columnExtensions={columnExtensions}
          withPagination={true}
          hasActions={true}
          actions={actions}
          noDataText="pas de groupement"
        />
      </Paper>

      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={() => handleModalClose()}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <AppBar className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <IconButton
              color="inherit"
              onClick={() => handleModalClose()}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" color="inherit" className={classes.flex}>
              Groupe n° {groupCode}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogTitle id="alert-dialog-title">Groupe n° {groupCode}</DialogTitle>
        <DialogContent>
          <Grid container spacing={0} className={classes.gridContainer}>
            <Grid item xs={4}>
              <HandleGroupForm
                groupCode={groupCode}
                onHandleModalClose={(action) => handleModalClose(action)}
                readOnly={userRole !== "master" && userRole !== "superuser"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleModalClose()} color="primary" autoFocus>
            fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={3000}
        open={snackBarOpen}
        onClose={handleSnackBarClose}
        ContentProps={{
          "aria-describedby": "message-id",
        }}
        message={<span id="message-id">{snackBarText}</span>}
      />
    </Page>
  );
};

export default Groups;
